import {
  createSearchParams,
  type DocumentMeta,
  type SearchDocumentNames,
  searchDocuments
} from "@prismicio/editor-fields"
import { last } from "@prismicio/editor-support/Array"
import { isScrolledToBottom } from "@prismicio/editor-support/DOM"
import { keys } from "@prismicio/editor-support/Object"
import { useTabFocus } from "@prismicio/editor-support/React"
import { cache, useRequest } from "@prismicio/editor-support/Suspense"
import { useEffect, useState } from "react"

import { env } from "../../app/env"
import { baseUrl as coreApiBaseUrl } from "../../app/services/core"
import {
  type SearchDocumentParams,
  useRepositoryLanguages,
  useSearchDocumentParams
} from "../useDocumentList"

interface useCacheRevalidationParams {
  searchTerm: string
  firstResult?: DocumentMeta
  onCacheInvalidated: () => void
}

function useCacheRevalidation(params: useCacheRevalidationParams) {
  const { searchTerm, firstResult, onCacheInvalidated } = params

  useTabFocus(() =>
    searchDocuments(
      coreApiBaseUrl,
      env.PAGE_BUILDER_AUTH_STRATEGY,
      searchTerm,
      1
    )
      .then(
        (response) =>
          response.results[0]?.id !== firstResult?.id && onCacheInvalidated()
      )
      .catch(() => undefined)
  )
}

interface GetStatusBasedOnTabArgs {
  tab: "working" | "archived" | "upcoming"
  releaseId?: string
}

function getStatusBasedOnTab(args: GetStatusBasedOnTabArgs) {
  const { tab, releaseId } = args

  if (tab === "upcoming" && !releaseId) {
    return ["release"]
  }

  if (tab === "upcoming" && releaseId) {
    return [`release:${releaseId}`]
  }

  if (tab === "working") {
    return ["unclassified", "published"]
  }

  if (tab === "archived") {
    return ["archived"]
  }

  return []
}

interface useDocumentDataSearchParams {
  tab: "working" | "archived" | "upcoming"
  releaseId?: string
  onCacheInvalidated: () => void
}

export function useDocumentDataSearch(params: useDocumentDataSearchParams) {
  const { onCacheInvalidated, tab, releaseId } = params

  const [cursor, setCursor] = useState("")
  const [documents, setDocuments] = useState<DocumentMeta[]>([])
  const [previousSearchTerm, setPreviousSearchTerm] = useState("")

  const { searchDocumentParams } = useSearchDocumentParams()
  const { masterLocaleId } = useRepositoryLanguages()
  const searchDocumentsTerm = getSearchDocumentsTerm({
    ...{
      status: getStatusBasedOnTab({
        tab,
        releaseId
      }),
      language: [masterLocaleId]
    },
    ...searchDocumentParams
  })

  useCacheRevalidation({
    searchTerm: searchDocumentsTerm,
    firstResult: documents[0],
    onCacheInvalidated
  })

  function refresh() {
    cache.clear(searchDocuments)

    setDocuments([])
    setCursor("")
    setPreviousSearchTerm("")
  }

  const response = useRequest(searchDocuments, [
    coreApiBaseUrl,
    env.PAGE_BUILDER_AUTH_STRATEGY,
    searchDocumentsTerm,
    limit,
    searchDocumentsTerm === previousSearchTerm ? cursor : ""
  ])

  if (searchDocumentsTerm !== previousSearchTerm) {
    // The search term or filters have changed, so the state should reset to avoid
    // duplicate documents in the list.
    setPreviousSearchTerm(searchDocumentsTerm)
    setCursor("")
    setDocuments(response.results)
  } else if (
    response.results.length > 0 &&
    last(documents)?.id !== last(response.results)?.id
  ) {
    setDocuments((currDocuments) => currDocuments.concat(response.results))
  }

  const hasNextPage = response.total !== documents.length

  function fetchNextPage() {
    if (response.cursor === undefined) return

    setCursor(response.cursor)
  }

  return {
    documents,
    hasNextPage,
    fetchNextPage,
    response,
    refresh
  }
}

export function usePageBottom(onReachedBottom: () => void) {
  useEffect(() => {
    function handleScroll() {
      const hasReachedBottom = isScrolledToBottom(document.documentElement)
      if (hasReachedBottom) onReachedBottom()
    }

    window.addEventListener("wheel", handleScroll)
    window.addEventListener("scroll", handleScroll)

    return () => {
      window.removeEventListener("wheel", handleScroll)
      window.removeEventListener("scroll", handleScroll)
    }
  }, [onReachedBottom])
}

export function getSearchDocumentsTerm(
  searchDocumentParams: SearchDocumentParams
) {
  const params = keys(searchDocumentParams).flatMap((name) => {
    const values = searchDocumentParams[name]
    if (!values) return []
    return { name, values }
  })

  const searchDocumentsTerm = new URLSearchParams(
    createSearchParams<SearchDocumentNames>(params)
  ).toString()

  return searchDocumentsTerm
}

const limit = 30
